<template>
  <div>
    <serveur-info :erreur="error" />
    <my-notification :icon="'train'">
      <template #title>
        {{ $t('compCarbonFormsTravelsMain.title') }}
      </template>
      <p>
        {{ $t('compCarbonFormsTravelsMain.info-1') }}
        <strong>{{ GHGIYear }}</strong
        >, {{ $t('compCarbonFormsTravelsMain.info-1b') }}.
        {{ $t('compCarbonFormsTravelsMain.info-2') }}
      </p>
    </my-notification>
    <br />

    <help-file-format :module="module" />

    <b-notification :has-icon="true" icon="info" :closable="false">
      <span v-html="$t('compCarbonFormsTravelsMain.info-3')" />
      <br /><br />
      <span v-html="$t('compCarbonFormsTravelsMain.info-9')" />
    </b-notification>

    <b-loading :is-full-page="true" v-model="isLoading" :can-cancel="false" />
    <b-notification
      type="is-warning"
      v-if="formats.includes('geslab')"
      has-icon
    >
      <p v-html="$t('compCarbonFormsTravelsMain.geslab-warning')"></p>
    </b-notification>
    <upload
      v-model="source"
      :disabled="isSubmitted"
      :module="module"
      :infos="infos"
      :callback="callback"
      :formatDescription="formatDescription"
      :download="download"
      @update="$emit('is-modified', true)"
    />

    <travels-table
      :items="travels"
      @edit="edit"
      @update="$emit('is-modified', true)"
      @del="del"
      data-testid="travels-table"
    />

    <b-modal
      v-if="travel !== null"
      :active.sync="displayModal"
      has-modal-card
      :can-cancel="['escape', 'x', 'outside']"
      :width="1200"
    >
      <div class="modal-card" style="width: 1200px">
        <header class="modal-card-head">
          <p v-if="modeCreation" class="modal-card-title">
            {{ $t('compCarbonFormsTravelsMain.info-12') }}
          </p>
          <p v-else class="modal-card-title">
            {{ $t('compCarbonFormsTravelsMain.info-13') }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-notification :closable="false">
            {{ $t('compCarbonFormsTravelsMain.info-14') }}
          </b-notification>
          <travel-form
            :travel="travel"
            :modeCreation="modeCreation"
            @close="closeModal"
            @update="update"
            @add="add"
          />
        </section>
      </div>
    </b-modal>

    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="buttons">
          <b-button
            class="button"
            icon-left="plus"
            :disabled="isSubmitted"
            @click="displayAddForm()"
          >
            {{ $t('add') }}
          </b-button>
          <b-button
            class="level-item"
            :icon-left="validSavedIcon"
            :disabled="!flags.isModified"
            type="is-primary"
            @click="validate()"
            data-testid="travels-save"
          >
            <span v-if="authStore.isAuthenticated">{{ $t('save') }}</span>
            <span v-else>{{ $t('validate') }}</span>
          </b-button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import Travel from '@/models/carbon/Travel.js'
import Modules from '@/models/Modules.js'
import { createExportFile } from '@/utils/files'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useCoreStore } from '@/stores/core.js'
import { useAdminStore } from '@/stores/admin.js'
import { GeonamesDecoder } from '@/utils/geonames'
import ges1p5format from '@/components/carbon/forms/travels/formats/ges1p5/ges1p5.js'

export default {
  name: 'Main',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    upload: () => import('@/components/carbon/forms/Upload.vue'),
    'help-file-format': () =>
      import('@/components/carbon/forms/HelpFileFormat.vue'),
    'travel-form': () => import('@/components/carbon/forms/travels/Form.vue'),
    'travels-table': () => import('@/components/carbon/forms/travels/Table.vue')
  },
  props: {
    flags: {
      type: Object
    }
  },
  data () {
    return {
      error: '',
      source: null,
      isLoading: false,
      geonamesCache: [],
      displayModal: false,
      travel: null,
      modeCreation: false,
      geonamesDecoder: null,
      formatDescriptions: []
    }
  },
  setup () {
    const authStore = useAuthenticationStore()
    const adminStore = useAdminStore()
    const ghgiStore = useGHGIStore()
    const coreStore = useCoreStore()
    return { authStore, adminStore, ghgiStore, coreStore }
  },
  mounted () {
    this.geonamesDecoder = new GeonamesDecoder(
      this.coreStore.setting('global', 'GEONAMES_USERNAME').value,
      this.$i18n.locale
    )
  },
  computed: {
    travelPurposeDictionary: function () {
      return this.coreStore.travelPurposeDictionary
    },
    travelPositionDictionary: function () {
      return this.coreStore.travelPositionDictionary
    },
    validSavedIcon: function () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    module: function () {
      return Modules.TRAVELS
    },
    travelClass: function () {
      return this.coreStore.module(Modules.TRAVELS).klass
    },
    formats: function () {
      return this.coreStore.setting('travels', 'FILE_FORMATS').value
    },
    formatDescription: function () {
      let self = this
      return function (fields) {
        for (let format of self.formats) {
          if (
            self.formatDescriptions[format]
              .default(
                self.travelPositionDictionary,
                self.travelPurposeDictionary
              )
              .meta.check(fields)
          ) {
            return [
              self.formatDescriptions[format].default(
                self.travelPositionDictionary,
                self.travelPurposeDictionary
              ),
              format
            ]
          }
        }
        return [
          ges1p5format(
            self.travelPositionDictionary,
            self.travelPurposeDictionary
          ),
          'ges1p5'
        ]
      }
    },
    infos: function () {
      let value1 =
        '<i class="fa fa-times" style="font-size:26px;color:#f14668;"></i> ' +
        this.$t('no')
      if (this.travels.length === 0) {
        value1 = '-'
      } else if (this.hasPurposeOption()) {
        value1 =
          '<i class="fa fa-check" style="font-size:26px;color:#48c774;"></i> ' +
          this.$t('yes')
      }
      let value2 =
        '<i class="fa fa-times" style="font-size:26px;color:#f14668;"></i> ' +
        this.$t('no')
      if (this.travels.length === 0) {
        value2 = '-'
      } else if (this.hasStatusOption()) {
        value2 =
          '<i class="fa fa-check" style="font-size:26px;color:#48c774;"></i> ' +
          this.$t('yes')
      }
      return [
        {
          label: this.$t('compCarbonFormsTravelsMain.label-1'),
          value: this.nbValidTravels() + ' / ' + this.nbTravels()
        },
        {
          label: this.$t('compCarbonFormsTravelsMain.label-2'),
          value: value1
        },
        {
          label: this.$t('compCarbonFormsTravelsMain.label-3'),
          value: value2
        }
      ]
    },
    GHGIYear: function () {
      return this.ghgiStore.item.year
    },
    hasBeenLoaded: function () {
      let loaded = false
      if (!this.dropFile.name) {
        if (this.ghgiStore.hasItems(this.module) && this.flags.isValid) {
          loaded = true
        }
      }
      return loaded
    },
    travels: function () {
      return this.ghgiStore.items(this.module).filter((item) => {
        if (this.source === null) {
          return true
        } else {
          return item.source === this.source
        }
      })
    },
    travelsLoadedFromDatabase: function () {
      return (
        this.ghgiStore.countItems({
          module: this.module,
          accessor: (travel) => (travel.isFromDatabase() ? 1 : 0)
        }) !== 0
      )
    }
  },
  methods: {
    initformatDescriptions: async function () {
      for (let format of this.formats) {
        this.formatDescriptions[format] = await import(
          `@/components/carbon/forms/travels/formats/${format}/${format}.js`
        )
      }
    },
    callback: async function (data, format, source) {
      this.error = null
      this.isLoading = true
      let allTags = this.adminStore.allTagCategories
        .map((obj) => obj.tags)
        .flat()
      let sections = await this.travelClass.createFromLines(
        data,
        format,
        allTags,
        this.geonamesDecoder,
        source,
        this.travelPositionDictionary,
        this.travelPurposeDictionary
      )
      for (let section of sections) {
        // this will add this section to the corresponding travel
        this.ghgiStore.addItem({
          module: this.module,
          item: section
        })
      }
      this.ghgiStore.sortItems(this.module)
      this.isLoading = false
    },
    nbValidTravels: function () {
      let testFunc = (t) => {
        if (this.source) {
          return t.isValid() && t.source === this.source ? t.amount : 0
        } else {
          return t.isValid() ? t.amount : 0
        }
      }
      return this.ghgiStore.countItems({
        module: this.module,
        accessor: testFunc
      })
    },
    nbTravels: function () {
      let testFunc = (t) => {
        if (this.source) {
          return t.source === this.source ? t.amount : 0
        } else {
          return t.amount
        }
      }
      return this.ghgiStore.countItems({
        module: this.module,
        accessor: testFunc
      })
    },
    hasPurposeOption: function () {
      return Travel.hasPurposeOption(this.travels)
    },
    hasStatusOption: function () {
      return Travel.hasStatusOption(this.travels)
    },
    download: function () {
      let csvContent =
        'mission\tDate de départ\tVille de départ\tPays de départ\t'
      csvContent +=
        'Ville de destination\tPays de destination\tMode de déplacement\t'
      csvContent +=
        'Nb de personnes dans la voiture\tAller Retour (OUI si identiques, NON si différents)\t'
      csvContent +=
        "Motif du déplacement (optionnel)\tStatut de l'agent (optionnel)\t"
      csvContent += 'distance.km\temission.kg.co2e\tuncertainty.kg.co2e\n'
      for (let travel of this.travels) {
        for (let section of travel.sections) {
          let intensity = section.getCarbonIntensity(this.GHGIyear)
          csvContent +=
            travel.names.join(',') +
            '\t' +
            travel.date +
            '\t' +
            section.departureCity +
            '\t'
          csvContent +=
            section.departureCountry +
            '\t' +
            section.destinationCity +
            '\t' +
            section.destinationCountry +
            '\t'
          csvContent +=
            section.transportation + '\t' + section.carpooling + '\t'
          if (section.isRoundTrip) {
            csvContent += 'oui\t'
          } else {
            csvContent += 'non\t'
          }
          csvContent += travel.purpose + '\t' + travel.status + '\t'
          csvContent += parseInt(section.getDistance()) + '\t'
          csvContent +=
            parseInt(intensity.intensity) +
            '\t' +
            parseInt(intensity.uncertainty) +
            '\n'
        }
      }
      createExportFile(csvContent, this.module)
    },
    closeModal: function () {
      this.displayModal = false
      this.travel = null
    },
    update: function (obj) {
      this.ghgiStore.updateItem({
        module: this.module,
        item: obj
      })
      this.$emit('is-modified', true)
      this.closeModal()
    },
    add: function (travel) {
      // FIXME(msimonin): after moving code around I'm not sure that sorting is
      // still required
      if (travel.sections.length > 0) {
        travel.sortSections()
      }
      this.ghgiStore.addItem({
        module: this.module,
        item: travel
      })
      this.$emit('is-modified', true)
      this.closeModal()
    },
    del: function (items) {
      this.ghgiStore.deleteItems({
        module: this.module,
        items: items
      })
    },
    edit: function (travel) {
      this.modeCreation = false
      this.travel = travel
      this.displayModal = true
    },
    displayAddForm: function () {
      this.modeCreation = true
      this.travel = this.travelClass.createEmpty()
      this.displayModal = true
    },
    validate: function () {
      let self = this
      if (this.ghgiStore.invalidItems(this.module).length !== 0) {
        this.$buefy.dialog.confirm({
          title: self.$t('compCarbonFormsTravelsMain.error-msg-5'),
          message: self.$t('compCarbonFormsTravelsMain.error-msg-6'),
          confirmText: self.$t('validate'),
          cancelText: self.$t('cancel'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: function () {
            self.ghgiStore.deleteInvalidItems(self.module)
            self.subValidate()
          }
        })
      } else {
        this.subValidate()
      }
    },
    subValidate: function () {
      if (this.authStore.isAuthenticated) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        })
        this.ghgiStore
          .saveItems(this.module)
          .then((data) => {
            loadingComponent.close()
            this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
            this.$emit('is-modified', false)
            this.error = ''
            window.scrollTo(0, 0)
            this.$buefy.toast.open({
              type: 'is-light',
              message: this.$t('compCarbonFormsTravelsMain.save-msg')
            })
            this.source = null
          })
          .catch((error) => {
            loadingComponent.close()
            this.$emit('is-valid', false)
            this.error = error.response.data
          })
      } else {
        this.$emit('is-valid', this.ghgiStore.items(this.module).length > 0)
        this.$emit('is-modified', false)
      }
    }
  },
  watch: {
    dropFile: function () {
      // if file size > 500 ko warn user for processing time
      const fileSize = this.dropFile.size
      const maxSize = 1024 * 512 // 500 ko
      let self = this
      if (fileSize > maxSize) {
        this.$buefy.dialog.confirm({
          title: self.$t('file-size.title'),
          message: self.$t('file-size.msg'),
          confirmText: self.$t('validate'),
          cancelText: self.$t('cancel'),
          onConfirm: () => self.processFile()
        })
      } else {
        this.processFile()
      }
    },
    formats: function () {
      this.initformatDescriptions()
    }
  },
  beforeMount () {
    this.initformatDescriptions()
  }
}
</script>
